import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { generateLevel, generateLevelAsync } from '../utils/levelGenerator';
import { canMove, getMaxMove, isLevelSolvable } from '../utils/solver';
import { GRID_COLS, GRID_ROWS } from '../utils/constants';

function cloneBlocks(blocks) {
  return blocks.map(b => ({ ...b }));
}

function difficultyForLevel(levelNumber) {
  const n = Number(levelNumber) || 1;
  if (n <= 10) return 'easy';
  if (n <= 25) return 'medium';
  return 'hard';
}

export function useUnblockGame() {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [difficulty, setDifficulty] = useState(() => difficultyForLevel(1));
  const [levelData, setLevelData] = useState(() => generateLevel(difficultyForLevel(1)));
  const [initialBlocks, setInitialBlocks] = useState(() => cloneBlocks(levelData.blocks));
  const [playState, setPlayState] = useState(() => ({
    blocks: cloneBlocks(levelData.blocks),
    moveHistory: [],
    moveCount: 0,
  }));

  const { blocks, moveHistory, moveCount } = playState;

  const nextLevelCacheRef = useRef(null);
  const prefetchTokenRef = useRef(0);

  useEffect(() => {
    const nextLevelNum = currentLevel + 1;
    const nextDiff = difficultyForLevel(nextLevelNum);

    if (nextDiff === 'hard') {
      return undefined;
    }

    const tid = window.setTimeout(() => {
      const data = generateLevel(nextDiff);
      if (!isLevelSolvable(data.blocks).solvable) return;
      nextLevelCacheRef.current = {
        level: nextLevelNum,
        difficulty: nextDiff,
        data,
      };
    }, 300);
    return () => window.clearTimeout(tid);
  }, [currentLevel]);

  const isComplete = useMemo(() => {
    const prisoner = blocks.find(b => b.isPrisoner);
    if (!prisoner) return false;
    return prisoner.x + prisoner.width >= GRID_COLS;
  }, [blocks]);

  useEffect(() => {
    if (!isComplete) return undefined;

    const nextLevelNum = currentLevel + 1;
    const nextDiff = difficultyForLevel(nextLevelNum);
    if (nextDiff !== 'hard') return undefined;

    if (
      nextLevelCacheRef.current?.level === nextLevelNum &&
      nextLevelCacheRef.current?.difficulty === nextDiff
    ) {
      return undefined;
    }

    const token = prefetchTokenRef.current + 1;
    prefetchTokenRef.current = token;
    let cancelled = false;

    (async () => {
      const data = await generateLevelAsync(nextDiff);
      if (cancelled || prefetchTokenRef.current !== token) return;
      if (!isLevelSolvable(data.blocks).solvable) return;
      nextLevelCacheRef.current = {
        level: nextLevelNum,
        difficulty: nextDiff,
        data,
      };
    })();

    return () => {
      cancelled = true;
    };
  }, [isComplete, currentLevel]);
  
  const optimalMoves = levelData.optimalMoves;
  
  const moveBlock = useCallback((blockId, dx, dy) => {
    setPlayState(prev => {
      const currentBlocks = prev.blocks;
      const block = currentBlocks.find(b => b.id === blockId);
      if (!block) return prev;
      
      if (block.orientation === 'H' && dy !== 0) return prev;
      if (block.orientation === 'V' && dx !== 0) return prev;
      
      if (!canMove(block, dx, dy, currentBlocks)) return prev;
      
      return {
        blocks: currentBlocks.map(b =>
          b.id === blockId ? { ...b, x: b.x + dx, y: b.y + dy } : b
        ),
        moveHistory: [...prev.moveHistory, cloneBlocks(currentBlocks)],
        moveCount: prev.moveCount + 1,
      };
    });
  }, []);
  
  const moveBlockToPosition = useCallback((blockId, newX, newY) => {
    setPlayState(prev => {
      const currentBlocks = prev.blocks;
      const block = currentBlocks.find(b => b.id === blockId);
      if (!block) return prev;
      
      const dx = newX - block.x;
      const dy = newY - block.y;
      
      if (block.orientation === 'H' && dy !== 0) return prev;
      if (block.orientation === 'V' && dx !== 0) return prev;
      
      const testBlock = { ...block, x: newX, y: newY };
      const otherBlocks = currentBlocks.filter(b => b.id !== blockId);
      
      if (newX < 0 || newY < 0) return prev;
      if (newX + block.width > GRID_COLS || newY + block.height > GRID_ROWS) return prev;
      
      for (const other of otherBlocks) {
        const collision = 
          testBlock.x < other.x + other.width &&
          testBlock.x + testBlock.width > other.x &&
          testBlock.y < other.y + other.height &&
          testBlock.y + testBlock.height > other.y;
        if (collision) return prev;
      }
      
      if (dx === 0 && dy === 0) return prev;
      
      return {
        blocks: currentBlocks.map(b =>
          b.id === blockId ? { ...b, x: newX, y: newY } : b
        ),
        moveHistory: [...prev.moveHistory, cloneBlocks(currentBlocks)],
        moveCount: prev.moveCount + 1,
      };
    });
  }, []);
  
  const getBlockMoveRange = useCallback((blockId) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return { min: 0, max: 0 };
    
    if (block.orientation === 'H') {
      const maxLeft = getMaxMove(block, -1, 0, blocks);
      const maxRight = getMaxMove(block, 1, 0, blocks);
      return {
        minX: block.x - maxLeft,
        maxX: block.x + maxRight,
        minY: block.y,
        maxY: block.y,
      };
    } else {
      const maxUp = getMaxMove(block, 0, -1, blocks);
      const maxDown = getMaxMove(block, 0, 1, blocks);
      return {
        minX: block.x,
        maxX: block.x,
        minY: block.y - maxUp,
        maxY: block.y + maxDown,
      };
    }
  }, [blocks]);
  
  const undo = useCallback(() => {
    setPlayState(prev => {
      if (prev.moveHistory.length === 0) return prev;
      const previousState = prev.moveHistory[prev.moveHistory.length - 1];
      return {
        blocks: previousState,
        moveHistory: prev.moveHistory.slice(0, -1),
        moveCount: Math.max(0, prev.moveCount - 1),
      };
    });
  }, []);
  
  const restart = useCallback(() => {
    setPlayState({
      blocks: cloneBlocks(initialBlocks),
      moveHistory: [],
      moveCount: 0,
    });
  }, [initialBlocks]);
  
  const applyLevelData = useCallback((safeLevel, nextDifficulty, newLevelData) => {
    setDifficulty(nextDifficulty);
    setLevelData(newLevelData);
    setInitialBlocks(cloneBlocks(newLevelData.blocks));
    setPlayState({
      blocks: cloneBlocks(newLevelData.blocks),
      moveHistory: [],
      moveCount: 0,
    });
    setCurrentLevel(safeLevel);
  }, []);

  const resolveSolvableLevel = useCallback(async (difficulty, preferredData = null) => {
    const maxAttempts = 5;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const candidate = attempt === 0 && preferredData
        ? preferredData
        : difficulty === 'hard'
          ? await generateLevelAsync(difficulty)
          : generateLevel(difficulty);

      if (isLevelSolvable(candidate.blocks).solvable) {
        return candidate;
      }
    }

    return difficulty === 'hard'
      ? await generateLevelAsync(difficulty)
      : generateLevel(difficulty);
  }, []);

  const goToLevel = useCallback(async (levelNumber) => {
    const safeLevel = Math.max(1, Number(levelNumber) || 1);
    const nextDifficulty = difficultyForLevel(safeLevel);

    let preferredData = null;
    if (
      nextLevelCacheRef.current &&
      nextLevelCacheRef.current.level === safeLevel &&
      nextLevelCacheRef.current.difficulty === nextDifficulty
    ) {
      preferredData = nextLevelCacheRef.current.data;
      nextLevelCacheRef.current = null;
    }

    const newLevelData = await resolveSolvableLevel(nextDifficulty, preferredData);
    applyLevelData(safeLevel, nextDifficulty, newLevelData);
  }, [applyLevelData, resolveSolvableLevel]);

  const nextLevel = useCallback(async () => {
    await goToLevel(currentLevel + 1);
  }, [currentLevel, goToLevel]);
  
  return {
    blocks,
    moveCount,
    isComplete,
    currentLevel,
    difficulty,
    optimalMoves,
    canUndo: moveHistory.length > 0,
    moveBlock,
    moveBlockToPosition,
    getBlockMoveRange,
    undo,
    restart,
    nextLevel,
    goToLevel,
  };
}
